import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';

const focusableSelector = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])'
].join(',');

export interface OperationStatusItem {
  label: string;
  value: ReactNode;
}

export interface OperationModalAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}

export type OperationModalTone = 'working' | 'success' | 'error' | 'warning';

export function OperationModal({
  active,
  tone = 'working',
  className = '',
  icon,
  titleId,
  ariaLabel,
  eyebrow,
  title,
  body,
  detail,
  error,
  code,
  statusItems = [],
  actions = []
}: {
  active: boolean;
  tone?: OperationModalTone;
  className?: string;
  icon?: ReactNode;
  titleId?: string;
  ariaLabel?: string;
  eyebrow: string;
  title: string;
  body: string;
  detail?: ReactNode;
  error?: ReactNode;
  code?: string | null;
  statusItems?: OperationStatusItem[];
  actions?: OperationModalAction[];
}) {
  const id = titleId || undefined;
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const previousActiveElement = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const firstFocusable = modalRef.current?.querySelector<HTMLElement>(focusableSelector);
    (firstFocusable || modalRef.current)?.focus({ preventScroll: true });
    return () => {
      previousActiveElement?.focus({ preventScroll: true });
    };
  }, []);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Tab') return;
    const focusable = Array.from(modalRef.current?.querySelectorAll<HTMLElement>(focusableSelector) || []);
    if (!focusable.length) {
      event.preventDefault();
      modalRef.current?.focus({ preventScroll: true });
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus({ preventScroll: true });
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus({ preventScroll: true });
    }
  }

  return (
    <div
      className="admin-command-overlay operation-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={id}
      aria-label={id ? undefined : ariaLabel || title}
      onKeyDown={handleKeyDown}
    >
      <div
        ref={modalRef}
        className={`admin-command-modal operation-modal operation-${tone}${className ? ` ${className}` : ''}`}
        tabIndex={-1}
        aria-busy={active ? 'true' : 'false'}
      >
        {icon ? (
          <div className="operation-icon" aria-hidden="true">{icon}</div>
        ) : (
          <div className="admin-command-spinner operation-spinner" aria-hidden="true" data-active={active ? 'true' : 'false'} />
        )}
        <div className="admin-command-copy operation-copy">
          <span>{eyebrow}</span>
          <h2 id={id}>{title}</h2>
          <p>{body}</p>
        </div>
        {statusItems.length > 0 && (
          <div className="admin-command-status operation-status">
            {statusItems.map((item) => (
              <div key={item.label}>
                <span>{item.label}</span>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        )}
        {code && <code>{code}</code>}
        {detail && <p className="admin-command-detail operation-detail">{detail}</p>}
        {error && <p className="admin-command-error operation-error">{error}</p>}
        {actions.length > 0 && (
          <div className="admin-command-actions operation-actions">
            {actions.map((action) => (
              <button
                key={action.label}
                type="button"
                className={action.primary ? 'primary' : undefined}
                disabled={action.disabled}
                onClick={action.onClick}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
